import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, Rocket } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    id: "solo", name: "VeloClin Individual", price: 199, tagline: "Para o médico independente",
    features: [
      "Telemedicina estável + prontuário inteligente",
      "Sua página de vendas de consultas",
      "Receitas e exames digitais com validade nacional",
      "Pix direto na sua conta, sem intermediários",
      "Sem comissões sobre as suas consultas (taxa operacional de R$10,50 por pagamento recebido)",
      "Suporte humanizado e migração assistida gratuita",
    ],
  },
  {
    id: "basic", name: "VeloClin Clínica", price: 399, tagline: "Para clínicas e equipes", popular: true,
    features: [
      "Tudo do plano Individual",
      "Até 3 médicos + 1 secretária",
      "Agenda multiprofissional integrada",
      "Gestão financeira e relatórios da clínica",
      "Página da clínica e de cada profissional",
      "Assinatura fixa, sem comissões (R$10,50 por pagamento recebido)",
    ],
  },
  {
    id: "max", name: "VeloClin Multiprofissional", price: 899, tagline: "Para clínicas de múltiplos profissionais",
    features: [
      "Tudo do plano Clínica",
      "Médicos e secretárias ilimitados",
      "Telemedicina para toda a equipe",
      "Gestão financeira avançada (DRE e repasses)",
      "Suporte prioritário",
      "Assinatura fixa, sem comissões (R$10,50 por pagamento recebido)",
    ],
  },
];

export const Pricing = () => {
  const [annual, setAnnual] = useState(false);
  const [founders, setFounders] = useState(null);
  const priceOf = (p) => (annual ? Math.round(p.price * 10 / 12) : p.price);

  useEffect(() => {
    fetch(`${API}/api/founders/status`).then((r) => r.json()).then(setFounders).catch(() => {});
  }, []);

  return (
    <section id="precos" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-bold uppercase tracking-wider text-emerald-600">Planos</span>
          <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Escolha o plano ideal para a sua rotina
          </h2>
          <p className="mt-3 text-slate-600">Assinatura fixa, sem comissões sobre as suas consultas. Do médico individual às clínicas com múltiplos profissionais, em todo o Brasil. 14 dias grátis, sem cartão.</p>

          <div className="mt-6 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 p-1">
            <button data-testid="billing-mensal" onClick={() => setAnnual(false)} className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${!annual ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Mensal</button>
            <button data-testid="billing-anual" onClick={() => setAnnual(true)} className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${annual ? "bg-emerald-500 text-white shadow-sm" : "text-slate-500"}`}>Anual · 2 meses grátis</button>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {PLANS.map((p) => {
            const foundersActive = p.id === "solo" && founders?.active;
            return (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`relative flex flex-col rounded-3xl border-2 bg-white p-7 transition-all ${foundersActive ? "border-amber-400 shadow-[0_20px_50px_-15px_rgba(245,158,11,0.45)]" : p.popular ? "border-emerald-500 shadow-[0_20px_50px_-15px_rgba(16,185,129,0.45)] lg:-translate-y-3" : "border-slate-200 shadow-sm hover:border-emerald-200"}`}
            >
              {foundersActive && (
                <span data-testid="plan-solo-founder-badge" className="absolute -top-3.5 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-amber-500 px-4 py-1.5 text-xs font-bold text-white shadow">
                  <Rocket className="h-3.5 w-3.5" /> Fundadores VeloClin
                </span>
              )}
              {!foundersActive && p.popular && (
                <span className="absolute -top-3.5 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-bold text-white shadow">
                  <Sparkles className="h-3.5 w-3.5" /> Mais popular
                </span>
              )}
              <h3 className="font-heading text-xl font-extrabold text-slate-900">{p.name}</h3>
              <p className="mt-1 text-sm text-slate-500">{p.tagline}</p>
              {foundersActive ? (
                <>
                  <div className="mt-5 flex items-end gap-1.5">
                    <span className="text-sm font-semibold text-slate-400">R$</span>
                    <span data-testid={`plan-${p.id}-price`} className="font-heading text-5xl font-extrabold tracking-tight text-slate-900">{founders.price}</span>
                    <span className="mb-1.5 text-sm text-slate-500">/mês</span>
                    <span className="mb-1.5 ml-1 text-sm font-semibold text-slate-400 line-through">R$ {founders.full_price}</span>
                  </div>
                  <p data-testid="plan-solo-founder-remaining" className="mt-1 text-xs font-bold text-amber-600">Preço de lançamento por tempo limitado — os {founders.limit} primeiros médicos pagam R$ {founders.price}/mês, vitalício</p>
                </>
              ) : (
                <>
                  <div className="mt-5 flex items-end gap-1">
                    <span className="text-sm font-semibold text-slate-400">R$</span>
                    <span data-testid={`plan-${p.id}-price`} className="font-heading text-5xl font-extrabold tracking-tight text-slate-900">{priceOf(p)}</span>
                    <span className="mb-1.5 text-sm text-slate-500">/mês</span>
                  </div>
                  {annual && <p className="mt-1 text-xs font-semibold text-emerald-600">Cobrado anualmente</p>}
                </>
              )}

              <Link
                to={`/cadastro/solo?plan=${p.id}`}
                data-testid={`plan-${p.id}-cta`}
                className={`mt-6 flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-bold transition-all ${foundersActive ? "bg-amber-500 text-white shadow-[0_10px_24px_rgba(245,158,11,0.4)] hover:-translate-y-0.5 hover:bg-amber-600" : p.popular ? "bg-emerald-500 text-white shadow-[0_10px_24px_rgba(16,185,129,0.4)] hover:-translate-y-0.5 hover:bg-emerald-600" : "border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50"}`}
              >
                {foundersActive ? "Garantir minha vaga" : "Começar teste grátis"}
              </Link>

              <ul className="mt-7 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100"><Check className="h-3 w-3 text-emerald-600" strokeWidth={3} /></span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          );})}
        </div>
        <p className="mt-8 text-center text-sm text-slate-400">14 dias grátis em qualquer plano · sem cartão de crédito · cancele quando quiser</p>
      </div>
    </section>
  );
};
